import { useIntl } from 'react-intl';
import { Button } from 'primereact/button';
import './FilterParts.scss';

export interface ActiveFilterChip<K extends string = string> {
    /** Qué filtro quita el ⓧ del chip. */
    key: K;
    /** "Servicio: Guardia" */
    label: string;
}

interface ActiveFiltersProps<K extends string> {
    chips: ActiveFilterChip<K>[];
    onRemove: (key: K) => void;
    /** «Quitar todos»: aparece con más de un chip. */
    onClear: () => void;
}

/**
 * "Filtrando por": los filtros que están aplicados a la pantalla, cada uno con su ⓧ para quitarlo, y «Quitar todos»
 * cuando hay más de uno. Sin filtros no ocupa lugar. Lo comparten las pantallas con filtros al instante.
 */
export default function ActiveFilters<K extends string>({ chips, onRemove, onClear }: ActiveFiltersProps<K>) {
    const intl = useIntl();
    if (chips.length === 0) return null;
    return (
        <div className="nv-active-filters" role="group" aria-label={intl.formatMessage({ id: 'ActiveFiltersGroup' })}>
            <span className="nv-sr-only">{intl.formatMessage({ id: 'ActiveFiltersFilteringBy' })}</span>
            {chips.map(chip => (
                <span key={chip.key} className="nv-chip">
                    <span>{chip.label}</span>
                    <button
                        type="button"
                        className="nv-chip__remove nv-tip"
                        aria-label={intl.formatMessage({ id: 'ActiveFiltersRemoveOf' }, { filter: chip.label })}
                        data-pr-tooltip={intl.formatMessage({ id: 'ActiveFiltersRemove' })}
                        onClick={() => onRemove(chip.key)}
                    >
                        <i className="pi pi-times-circle" aria-hidden="true" />
                    </button>
                </span>
            ))}
            {chips.length > 1 && (
                <Button type="button" link label={intl.formatMessage({ id: 'ActiveFiltersRemoveAll' })} onClick={onClear} />
            )}
        </div>
    );
}
